import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import history from '../../../history';
import {
  createTravelersAutoClaim,
  deleteClaim,
  fetchClaimById,
  fetchEmployee,
  getLocationCodes,
  printClaim,
  updateClaim
} from '../../../api/v4';
import config from '../../../config/config';
import { setActiveIncidentRequest } from '../../../actions/incidents';
import { getActiveIncidentSelector } from '../../../selectors/incidents';
import { getMyUserId, getAllUsers } from '../../../selectors/users';
import { useActiveHeirarchy } from '../../../utils/useActiveHeirarchy';
import customToast from '../../../utils/customToast';
import { normalizePhone } from '../../../utils/formValidation';
import DeleteItemModal from '../../../components/Modal/deleteItemModal';
import HeaderAndFooter from '../../../components/HeaderAndFooter';
import Header from '../../../components/Header';
import InstructionCard from '../../../components/claims/InstructionCard';
import MissingRequiredModal from '../../../components/Modal/missingRequiredModal';
import Modal from '../../../components/Modal';
import LeftNav from '../../../components/LeftNav';
import { SubmitSaveForLaterCancelFooter } from '../../../components/Footer';
import SaveChangesModal from '../../../components/Modal/saveChangesModal';
import TravelersAutoClaimCard from '../../../components/claims/Cards/travelersAuto';

import styles from '../claimCreate.module.scss';

export default function TravelersAutoClaimNew() {
  const activeIncident = useSelector(getActiveIncidentSelector);
  const { activeCompany } = useActiveHeirarchy();
  const myUserId = useSelector(getMyUserId);
  const users = useSelector(getAllUsers);
  const driverEmployeeId =
    activeIncident?.answers?.[config.staticIds.autoDriverQuestion];
  let { claimId } = useParams();
  const dispatch = useDispatch();

  const [id, setId] = useState(null);
  const [missingRequired, setMissingRequired] = useState(false);
  const [missingModalOpen, setMissingModalOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [overviewInfo, setOverviewInfo] = useState({});
  const [creating, setCreating] = useState(true);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [saveChangesModal, setSaveChangesModal] = useState(false);
  const [submitModal, setSubmitModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [incidentId, setIncidentId] = useState();
  const [locationCodes, setLocationCodes] = useState([]);

  // Form sections based on Travelers Auto API structure
  const [contactInfo, setContactInfo] = useState({
    primaryContact: {
      firstName: '',
      lastName: '',
      email: '',
      phone: { phoneNumber: '', phoneType: 'M' }
    },
    reportedBy: {
      firstName: '',
      lastName: '',
      email: '',
      phone: { phoneNumber: '', phoneType: 'M' }
    },
    mailingAddress: {
      addressLine: '',
      cityName: '',
      state: '',
      zipCode: ''
    }
  });
  const [contactEditing, setContactEditing] = useState(false);

  const [lossLocation, setLossLocation] = useState({
    address: {
      addressLine: '',
      cityName: '',
      state: '',
      zipCode: ''
    },
    isCompanyAddress: false
  });
  const [lossLocationEditing, setLossLocationEditing] = useState(false);

  const [lossDetails, setLossDetails] = useState({
    description: '',
    hasInjuredPassengers: false,
    incidentOnly: false,
    ownershipTypeCode: 'O',
    primaryVehicleDamaged: false,
    primaryVehicleDrivable: true,
    vehicleAccidentDetails: {
      vehiclesInvolved: 1
    }
  });
  const [lossDetailsEditing, setLossDetailsEditing] = useState(false);

  const [primaryVehicle, setPrimaryVehicle] = useState({
    make: '',
    model: '',
    year: '',
    vin: '',
    type: 'A',
    owner: {
      firstName: '',
      lastName: '',
      email: '',
      phone: { phoneNumber: '', phoneExtn: '', phoneType: 'M' },
      address: {
        addressLine: '',
        cityName: '',
        state: '',
        zipCode: ''
      }
    }
  });
  const [primaryVehicleEditing, setPrimaryVehicleEditing] = useState(false);

  const [driverDetails, setDriverDetails] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: { phoneNumber: '', phoneExtn: '', phoneType: 'M' },
    address: {
      addressLine: '',
      cityName: '',
      state: '',
      zipCode: ''
    },
    injuryType: '',
    isDriverInjured: false
  });
  const [driverEditing, setDriverEditing] = useState(false);

  const [claimants, setClaimants] = useState([]);
  const [claimantsEditing, setClaimantsEditing] = useState(false);

  const [dateOfLoss, setDateOfLoss] = useState('');
  const [locationCode, setLocationCode] = useState('');

  const setClaimInfo = useCallback(
    claim => {
      setId(claim._id);
      setIsSubmitted(claim.isSubmitted);
      setIncidentId(claim.incidentId);
      setPermissions(claim.permissions || []);
      setOverviewInfo({
        claimNumber: claim.claimNumber,
        submissionDate: claim.submissionDate,
        insuredReportNumber: claim.insuredReportNumber
      });

      // Map contact info
      if (claim.contact) {
        setContactInfo({
          primaryContact: {
            firstName: claim.contact.primaryContact?.firstName || '',
            lastName: claim.contact.primaryContact?.lastName || '',
            email: claim.contact.primaryContact?.email || '',
            phone: {
              phoneNumber:
                claim.contact.primaryContact?.phone?.phoneNumber || '',
              phoneType: claim.contact.primaryContact?.phone?.phoneType || 'M'
            }
          },
          reportedBy: {
            firstName: claim.contact.reportedBy?.firstName || '',
            lastName: claim.contact.reportedBy?.lastName || '',
            email: claim.contact.reportedBy?.email || '',
            phone: {
              phoneNumber: claim.contact.reportedBy?.phone?.phoneNumber || '',
              phoneType: claim.contact.reportedBy?.phone?.phoneType || 'M'
            }
          },
          mailingAddress: {
            addressLine:
              claim.contact.mailingAddress?.address?.addressLine || '',
            cityName: claim.contact.mailingAddress?.address?.cityName || '',
            state: claim.contact.mailingAddress?.address?.state || '',
            zipCode: claim.contact.mailingAddress?.address?.zipCode || ''
          }
        });
      }

      // Map loss location
      if (claim.lossLocation) {
        setLossLocation({
          address: {
            addressLine: claim.lossLocation.address?.addressLine || '',
            cityName: claim.lossLocation.address?.cityName || '',
            state: claim.lossLocation.address?.state || '',
            zipCode: claim.lossLocation.address?.zipCode || ''
          },
          isCompanyAddress: claim.lossLocation.isCompanyAddress || false
        });
      }

      // Map date of loss and location code
      if (claim.dateOfLoss) {
        setDateOfLoss(claim.dateOfLoss);
      }
      if (claim.locationCode) {
        // Find the location code ID that matches the organization code
        const matchingCode = locationCodes.find(
          lc => lc.organizationCode === claim.locationCode
        );
        setLocationCode(matchingCode?._id || claim.locationCode);
      }

      // Map loss details
      if (claim.lossDetails) {
        setLossDetails({
          description: claim.lossDetails.description || '',
          hasInjuredPassengers: claim.lossDetails.hasInjuredPassengers || false,
          incidentOnly: claim.lossDetails.incidentOnly || false,
          ownershipTypeCode: claim.lossDetails.ownershipTypeCode || 'O',
          primaryVehicleDamaged:
            claim.lossDetails.primaryVehicleDamaged || false,
          primaryVehicleDrivable:
            claim.lossDetails.primaryVehicleDrivable || true,
          vehicleAccidentDetails: {
            vehiclesInvolved:
              claim.lossDetails.vehicleAccidentDetails?.vehiclesInvolved || 1,
            singleVehicleAccidentType:
              claim.lossDetails.vehicleAccidentDetails
                ?.singleVehicleAccidentType
          }
        });

        // Map primary vehicle details
        if (claim.lossDetails.primaryVehicleDetails) {
          const vehicle = claim.lossDetails.primaryVehicleDetails;
          setPrimaryVehicle({
            make: vehicle.make || '',
            model: vehicle.model || '',
            year: vehicle.year || '',
            vin: vehicle.vin || '',
            type: vehicle.type || 'A',
            owner: {
              firstName: vehicle.owner?.firstName || '',
              lastName: vehicle.owner?.lastName || '',
              email: vehicle.owner?.email || '',
              phone: {
                phoneNumber: vehicle.owner?.phone?.phoneNumber || '',
                phoneExtn: vehicle.owner?.phone?.phoneExtn || '',
                phoneType: vehicle.owner?.phone?.phoneType || 'M'
              },
              address: {
                addressLine: vehicle.owner?.address?.addressLine || '',
                cityName: vehicle.owner?.address?.cityName || '',
                state: vehicle.owner?.address?.state || '',
                zipCode: vehicle.owner?.address?.zipCode || ''
              }
            }
          });
        }

        // Map driver details
        if (claim.lossDetails.primaryVehicleDriverDetails) {
          const driver = claim.lossDetails.primaryVehicleDriverDetails;
          setDriverDetails({
            firstName: driver.firstName || '',
            lastName: driver.lastName || '',
            email: driver.email || '',
            phone: {
              phoneNumber: driver.phone?.phoneNumber || '',
              phoneExtn: driver.phone?.phoneExtn || '',
              phoneType: driver.phone?.phoneType || 'M'
            },
            address: {
              addressLine: driver.address?.addressLine || '',
              cityName: driver.address?.cityName || '',
              state: driver.address?.state || '',
              zipCode: driver.address?.zipCode || ''
            },
            injuryType: driver.injuryType || '',
            isDriverInjured: driver.isDriverInjured || false
          });
        }

        // Map claimants - flat structure per Travelers API spec
        if (claim.lossDetails.claimants) {
          setClaimants(
            claim.lossDetails.claimants.map(c => ({
              claimantType: c.claimantType || '',
              firstName: c.firstName || '',
              lastName: c.lastName || '',
              businessName: c.businessName || '',
              email: c.email || '',
              description: c.description || '',
              phone: {
                phoneNumber: c.phone?.phoneNumber || '',
                phoneExtn: c.phone?.phoneExtn || '',
                phoneType: c.phone?.phoneType || 'M'
              },
              address: {
                addressLine: c.address?.addressLine || '',
                cityName: c.address?.cityName || '',
                state: c.address?.state || '',
                zipCode: c.address?.zipCode || ''
              },
              injuryType: c.injuryType || '',
              relationshipToInsured: c.relationshipToInsured || '',
              make: c.make || '',
              model: c.model || '',
              year: c.year || '',
              owner: {
                firstName: c.owner?.firstName || '',
                lastName: c.owner?.lastName || '',
                email: c.owner?.email || '',
                phone: {
                  phoneNumber: c.owner?.phone?.phoneNumber || '',
                  phoneExtn: c.owner?.phone?.phoneExtn || '',
                  phoneType: c.owner?.phone?.phoneType || 'M'
                },
                address: {
                  addressLine: c.owner?.address?.addressLine || '',
                  cityName: c.owner?.address?.cityName || '',
                  state: c.owner?.address?.state || '',
                  zipCode: c.owner?.address?.zipCode || ''
                }
              },
              driver: {
                firstName: c.driver?.firstName || '',
                lastName: c.driver?.lastName || '',
                email: c.driver?.email || '',
                phone: {
                  phoneNumber: c.driver?.phone?.phoneNumber || '',
                  phoneExtn: c.driver?.phone?.phoneExtn || '',
                  phoneType: c.driver?.phone?.phoneType || 'M'
                },
                address: {
                  addressLine: c.driver?.address?.addressLine || '',
                  cityName: c.driver?.address?.cityName || '',
                  state: c.driver?.address?.state || '',
                  zipCode: c.driver?.address?.zipCode || ''
                },
                isDriverInjured: c.driver?.isDriverInjured || false,
                injuryType: c.driver?.injuryType || ''
              }
            }))
          );
        }
      }
    },
    [locationCodes]
  );

  useEffect(() => {
    if (!claimId) {
      setCreating(true);

      // Set permissions
      if (activeCompany) {
        setPermissions(activeCompany.claimPermissions || []);
      }

      // Pre-fill from incident if available
      if (activeIncident) {
        const answers = activeIncident?.answers;
        setIncidentId(activeIncident._id);

        // Get incident date
        if (answers?.[config.staticIds.timeIncidentOccured]) {
          setDateOfLoss(answers[config.staticIds.timeIncidentOccured]);
        }

        // Get incident location
        let incidentLocation =
          answers?.[config.staticIds.incidentLocation] &&
          !answers?.[config.staticIds.incidentLocation].includes(
            'Not Applicable'
          )
            ? answers?.[config.staticIds.incidentLocation]?.split(', ')
            : null;

        if (!incidentLocation) {
          const location = activeCompany?.groups?.find(
            g => g._id === activeIncident.groupId
          );
          if (location) {
            incidentLocation = [
              location.streetAddress,
              location.city,
              location.state,
              location.zip
            ];
          }
        }

        if (incidentLocation) {
          setLossLocation({
            address: {
              addressLine: incidentLocation[0] || '',
              cityName: incidentLocation[1] || '',
              state: incidentLocation[2] || '',
              zipCode:
                incidentLocation[3] === 'USA' ? '' : incidentLocation[3] || ''
            },
            isCompanyAddress: false
          });
        }

        // Get incident description
        if (answers?.[config.staticIds.incidentDescription]) {
          setLossDetails(prev => ({
            ...prev,
            description: answers[config.staticIds.incidentDescription]
          }));
        }

        // Fetch driver info if available
        if (driverEmployeeId) {
          const driverId = driverEmployeeId[0]?.value ?? driverEmployeeId[0];
          if (driverId) {
            fetchEmployee(driverId)
              .then(employee => {
                if (employee) {
                  setDriverDetails(prev => ({
                    ...prev,
                    firstName: employee.firstName || '',
                    lastName: employee.lastName || '',
                    email: employee.email || '',
                    phone: {
                      phoneNumber: normalizePhone(employee.phoneNumber) || '',
                      phoneExtn: '',
                      phoneType: 'M'
                    },
                    address: {
                      addressLine: employee.address?.streetAddress || '',
                      cityName: employee.address?.city || '',
                      state: employee.address?.stateName || '',
                      zipCode: employee.address?.zip || ''
                    }
                  }));
                }
              })
              .catch(err => {
                console.error('Error fetching driver employee:', err);
              });
          }
        }
      }
    } else {
      // Load existing claim
      fetchClaimById(claimId).then(claim => {
        setClaimInfo(claim);
        setCreating(false);
      });
    }
  }, [activeIncident, claimId, activeCompany, driverEmployeeId, setClaimInfo]);

  // Fetch location codes filtered to auto-eligible only
  useEffect(() => {
    getLocationCodes().then(codes => {
      const autoCodes = codes?.filter(c => c.isAuto) || [];
      setLocationCodes(autoCodes);
      if (autoCodes.length === 1) {
        setLocationCode(autoCodes[0]._id);
      }
    });
  }, []);

  // Helper to format phone for Travelers API (XXX-XXX-XXXX)
  const formatPhoneForAPI = phone => {
    if (!phone) return null;
    // Remove all non-digits
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return null; // Return null for invalid phones
  };

  // Helper to clean phone object - remove if no valid phone number
  const cleanPhone = phone => {
    if (!phone) return undefined;
    const formattedNumber = formatPhoneForAPI(phone.phoneNumber);
    if (!formattedNumber) return undefined;
    return {
      phoneNumber: formattedNumber,
      phoneType: phone.phoneType || 'M',
      ...(phone.phoneExtn ? { phoneExtn: phone.phoneExtn } : {})
    };
  };

  // Helper to format contact - remove empty emails, format phone
  const formatContact = contact => {
    if (!contact) return contact;
    const cleanedPhone = cleanPhone(contact.phone);
    return {
      firstName: contact.firstName || '',
      lastName: contact.lastName || '',
      ...(contact.email ? { email: contact.email } : {}), // Omit if empty
      ...(cleanedPhone ? { phone: cleanedPhone } : {})
    };
  };

  // Helper to format address - ensure state is 2-letter code, omit empty fields
  const formatAddress = address => {
    if (!address) return undefined;
    const hasContent =
      address.addressLine ||
      address.cityName ||
      address.state ||
      address.zipCode;
    if (!hasContent) return undefined;
    return {
      addressLine: address.addressLine || '',
      cityName: address.cityName || '',
      state: address.state?.substring(0, 2).toUpperCase() || '',
      zipCode: address.zipCode || ''
    };
  };

  // Helper to format owner with all required fields
  const formatOwner = owner => {
    if (!owner) return undefined;
    const cleanedPhone = cleanPhone(owner.phone);
    const cleanedAddress = formatAddress(owner.address);
    return {
      firstName: owner.firstName || '',
      lastName: owner.lastName || '',
      ...(owner.email ? { email: owner.email } : {}), // Omit if empty
      ...(cleanedPhone ? { phone: cleanedPhone } : {}),
      ...(cleanedAddress ? { address: cleanedAddress } : {})
    };
  };

  // Helper to format driver details - handle injuryType based on isDriverInjured
  const formatDriverDetails = driver => {
    if (!driver) return undefined;
    const cleanedPhone = cleanPhone(driver.phone);
    const cleanedAddress = formatAddress(driver.address);

    const result = {
      firstName: driver.firstName || '',
      lastName: driver.lastName || '',
      ...(driver.email ? { email: driver.email } : {}), // Omit if empty
      ...(cleanedPhone ? { phone: cleanedPhone } : {}),
      ...(cleanedAddress ? { address: cleanedAddress } : {}),
      isDriverInjured: driver.isDriverInjured || false
    };

    // Only include injuryType if driver is injured and has a value
    if (driver.isDriverInjured && driver.injuryType) {
      result.injuryType = driver.injuryType;
    }

    return result;
  };

  const buildSubmissionPayload = () => {
    // Look up the actual organization code from locationCodes
    const selectedLocationCode = locationCodes?.find(
      lc => lc._id === locationCode
    );
    const orgCode = selectedLocationCode?.organizationCode || '';

    // Format date as YYYY-MM-DD
    const formattedDate = dateOfLoss
      ? moment(dateOfLoss).format('YYYY-MM-DD')
      : '';

    // Determine vehicle accident details
    let vehiclesInvolved =
      parseInt(lossDetails.vehicleAccidentDetails?.vehiclesInvolved, 10) || 1;

    // If any Type A (Other Vehicle) claimant exists, there must be at least 2 vehicles
    const hasOtherVehicle = claimants.some(c => c.claimantType === 'A');
    if (hasOtherVehicle && vehiclesInvolved < 2) {
      vehiclesInvolved = 2;
    }

    // Build vehicle accident details based on number of vehicles
    // Travelers API returns Internal Error if singleVehicleAccidentType is sent with Type A claimants
    let vehicleAccidentDetails;
    if (vehiclesInvolved >= 2) {
      // Multi-vehicle accident - don't include singleVehicleAccidentType
      vehicleAccidentDetails = {
        vehiclesInvolved: vehiclesInvolved
      };
    } else {
      // Single vehicle - include type if provided
      vehicleAccidentDetails = {
        vehiclesInvolved: 1,
        singleVehicleAccidentType:
          lossDetails.vehicleAccidentDetails?.singleVehicleAccidentType || '3' // '3' = All Other Damages
      };
    }

    return {
      contact: {
        mailingAddress: {
          address: formatAddress(contactInfo.mailingAddress)
        },
        primaryContact: formatContact(contactInfo.primaryContact),
        reportedBy: formatContact(contactInfo.reportedBy)
      },
      dateOfLoss: formattedDate,
      locationCode: orgCode,
      lossDetails: {
        claimants: claimants
          .filter(c => c.firstName || c.lastName || c.businessName || c.make)
          .map(c => {
            // Build claimant with flat fields based on type (per Travelers API spec)
            if (c.claimantType === 'P') {
              // Property Damage claimant
              return {
                claimantType: 'P',
                ...(c.businessName ? { businessName: c.businessName } : {}),
                ...(c.description ? { description: c.description } : {}),
                ...(c.firstName ? { firstName: c.firstName } : {}),
                ...(c.lastName ? { lastName: c.lastName } : {}),
                ...(c.email ? { email: c.email } : {}),
                ...(formatAddress(c.address)
                  ? { address: formatAddress(c.address) }
                  : {})
              };
            } else if (c.claimantType === 'B') {
              // Bodily Injury claimant
              return {
                claimantType: 'B',
                ...(c.firstName ? { firstName: c.firstName } : {}),
                ...(c.lastName ? { lastName: c.lastName } : {}),
                ...(c.email ? { email: c.email } : {}),
                ...(cleanPhone(c.phone) ? { phone: cleanPhone(c.phone) } : {}),
                ...(c.injuryType ? { injuryType: c.injuryType } : {}),
                ...(c.relationshipToInsured
                  ? { relationshipToInsured: c.relationshipToInsured }
                  : {}),
                ...(formatAddress(c.address)
                  ? { address: formatAddress(c.address) }
                  : {})
              };
            } else if (c.claimantType === 'A') {
              // Other Vehicle (Auto Damage) claimant
              const driverObj = c.driver;
              let formattedDriver;
              if (driverObj?.firstName || driverObj?.lastName) {
                const driverPhone = cleanPhone(driverObj.phone);
                const driverAddr = formatAddress(driverObj.address);
                formattedDriver = {
                  firstName: driverObj.firstName || '',
                  lastName: driverObj.lastName || '',
                  ...(driverObj.email ? { email: driverObj.email } : {}),
                  ...(driverPhone ? { phone: driverPhone } : {}),
                  ...(driverAddr ? { address: driverAddr } : {}),
                  isDriverInjured: driverObj.isDriverInjured || false,
                  ...(driverObj.isDriverInjured && driverObj.injuryType
                    ? { injuryType: driverObj.injuryType }
                    : {})
                };
              }
              return {
                claimantType: 'A',
                ...(c.make ? { make: c.make } : {}),
                ...(c.model ? { model: c.model } : {}),
                ...(c.year ? { year: c.year } : {}),
                ...(formatOwner(c.owner)
                  ? { owner: formatOwner(c.owner) }
                  : {}),
                ...(formattedDriver ? { driver: formattedDriver } : {})
              };
            }
            // Fallback
            return { claimantType: c.claimantType };
          }),
        description: lossDetails.description,
        hasInjuredPassengers: lossDetails.hasInjuredPassengers || false,
        incidentOnly: lossDetails.incidentOnly || false,
        ownershipTypeCode: lossDetails.ownershipTypeCode || 'O',
        primaryVehicleDamaged: lossDetails.primaryVehicleDamaged || false,
        primaryVehicleDetails: {
          make: primaryVehicle.make || '',
          model: primaryVehicle.model || '',
          year: primaryVehicle.year || '',
          vin: primaryVehicle.vin || '',
          ...(primaryVehicle.type ? { type: primaryVehicle.type } : {}),
          ...(formatOwner(primaryVehicle.owner)
            ? { owner: formatOwner(primaryVehicle.owner) }
            : {})
        },
        primaryVehicleDrivable: lossDetails.primaryVehicleDrivable || false,
        primaryVehicleDriverDetails: formatDriverDetails(driverDetails),
        vehicleAccidentDetails: vehicleAccidentDetails
      },
      lossLocation: {
        ...(lossLocation.isCompanyAddress !== undefined
          ? { isCompanyAddress: lossLocation.isCompanyAddress }
          : {}),
        address: formatAddress(lossLocation.address)
      },
      state: selectedLocationCode?.state?.substring(0, 2).toUpperCase() || ''
    };
  };

  const handleSubmit = isSubmittedFlag => {
    const payload = buildSubmissionPayload();

    const apiCall = id
      ? updateClaim({ _id: id, ...payload, isSubmitted: isSubmittedFlag })
      : createTravelersAutoClaim({
          ...payload,
          incidentId,
          type: 'auto',
          isSubmitted: isSubmittedFlag,
          permissions
        });

    apiCall
      .then(response => {
        setSubmitModal(false);
        if (!response) return;
        setUnsavedChanges(false);
        setCreating(false);
        setEditingFalse();
        if (response?._id) {
          setId(response._id);
        }
        setOverviewInfo({
          claimNumber: response.claimNumber || overviewInfo.claimNumber,
          submissionDate:
            response.submissionDate || overviewInfo.submissionDate,
          insuredReportNumber:
            response.insuredReportNumber || overviewInfo.insuredReportNumber
        });
        if (isSubmittedFlag) {
          setIsSubmitted(true);
          customToast('Claim submitted successfully!', 'success');
        } else {
          customToast('Claim saved successfully!', 'success');
        }
      })
      .catch(error => {
        console.error('Error submitting claim:', error);
        const travelersError = error?.data?.travelersError;
        let errorMsg = 'Error submitting claim';
        if (travelersError) {
          errorMsg =
            travelersError?.error ||
            travelersError?.errorSummary ||
            travelersError?.message ||
            (Array.isArray(travelersError?.errors)
              ? travelersError.errors
                  .map(e => e.message || e.errorMessage || e)
                  .join('; ')
              : null) ||
            JSON.stringify(travelersError);
        } else if (error?.data?.message) {
          errorMsg = error.data.message;
        } else if (typeof error?.data === 'string') {
          errorMsg = error.data;
        }
        customToast(errorMsg, 'error');
        setSubmitModal(false);
      });
  };

  const setEditingFalse = () => {
    setContactEditing(false);
    setLossLocationEditing(false);
    setLossDetailsEditing(false);
    setPrimaryVehicleEditing(false);
    setDriverEditing(false);
    setClaimantsEditing(false);
    setUnsavedChanges(false);
  };

  const handleDelete = () => {
    deleteClaim(id).then(() => history.goBack());
  };

  const handleCancel = goBack => {
    if (goBack) {
      history.goBack();
      return;
    }
    if (
      id &&
      (contactEditing ||
        lossLocationEditing ||
        lossDetailsEditing ||
        primaryVehicleEditing ||
        driverEditing ||
        claimantsEditing)
    ) {
      setEditingFalse();
    } else if (unsavedChanges && !saveChangesModal) {
      setSaveChangesModal(true);
    } else {
      history.goBack();
    }
  };

  // Validation checks
  const contactInfoComplete =
    !!contactInfo.primaryContact.firstName &&
    !!contactInfo.primaryContact.lastName &&
    !!contactInfo.primaryContact.email &&
    !!contactInfo.primaryContact.phone.phoneNumber &&
    !!contactInfo.reportedBy.firstName &&
    !!contactInfo.reportedBy.lastName &&
    !!contactInfo.reportedBy.email &&
    !!contactInfo.reportedBy.phone?.phoneNumber &&
    !!contactInfo.mailingAddress.addressLine &&
    !!contactInfo.mailingAddress.cityName &&
    !!contactInfo.mailingAddress.state &&
    !!contactInfo.mailingAddress.zipCode;

  const lossLocationComplete =
    !!lossLocation.address.addressLine &&
    !!lossLocation.address.cityName &&
    !!lossLocation.address.state &&
    !!lossLocation.address.zipCode;

  const lossDetailsComplete =
    !!dateOfLoss &&
    !!locationCode &&
    !!lossDetails.description &&
    lossDetails.hasInjuredPassengers !== undefined &&
    !!lossDetails.ownershipTypeCode;

  const primaryVehicleComplete =
    !!primaryVehicle.make &&
    !!primaryVehicle.model &&
    !!primaryVehicle.year &&
    !!primaryVehicle.type &&
    !!primaryVehicle.vin &&
    primaryVehicle.vin?.length === 17 &&
    !!primaryVehicle.owner?.firstName &&
    !!primaryVehicle.owner?.lastName &&
    !!primaryVehicle.owner?.email &&
    !!primaryVehicle.owner?.phone?.phoneNumber &&
    !!primaryVehicle.owner?.address?.addressLine &&
    !!primaryVehicle.owner?.address?.cityName &&
    !!primaryVehicle.owner?.address?.state &&
    !!primaryVehicle.owner?.address?.zipCode;

  const driverDetailsComplete =
    !!driverDetails.firstName &&
    !!driverDetails.lastName &&
    !!driverDetails.phone?.phoneNumber &&
    !!driverDetails.address?.addressLine &&
    !!driverDetails.address?.cityName &&
    !!driverDetails.address?.state &&
    !!driverDetails.address?.zipCode;

  const canSubmit =
    contactInfoComplete &&
    lossLocationComplete &&
    lossDetailsComplete &&
    primaryVehicleComplete &&
    driverDetailsComplete;

  const isReadOnly = permissions?.find(p => p.userId === myUserId)?.readOnly;

  const print = () => {
    printClaim(id).then(response => window.open(response, '_blank'));
  };

  const header = (
    <Header
      title={activeIncident?.incidentNumber || 'New Auto Claim'}
      section="Travelers Auto Claim"
      pageActionOptions={[
        {
          label: 'Permissions',
          visible: !creating && !isReadOnly,
          color: 'blueOutline',
          onClick: () => history.push(`/app/permissions/Claim/${id}`)
        },
        {
          label: 'Return to Incident',
          visible: !!activeIncident,
          color: 'blueOutline',
          onClick: () =>
            dispatch(setActiveIncidentRequest(incidentId ?? activeIncident._id))
        },
        {
          label: 'Download Claim',
          visible: isSubmitted,
          color: 'greenOutline',
          onClick: print
        },
        {
          label: 'Delete Claim',
          visible: !creating && isSubmitted && !isReadOnly,
          color: 'redOutline',
          onClick: () => setDeleteModal(true)
        }
      ]}
      clickBack={() =>
        unsavedChanges ? setSaveChangesModal(true) : history.goBack()
      }
      needsSaved={unsavedChanges}
    />
  );

  const footer = (
    <SubmitSaveForLaterCancelFooter
      submitButtonClick={() =>
        canSubmit ? setSubmitModal(true) : setMissingModalOpen(true)
      }
      saveButtonClick={() => handleSubmit(false)}
      cancelButtonClick={handleCancel}
      onMouseEnter={() => setMissingRequired(true)}
    />
  );

  const left = (
    <LeftNav
      items={[
        {
          value: 0,
          label: isSubmitted ? 'Overview' : 'Instructions',
          complete: true
        },
        {
          value: 1,
          label: 'Contact Information',
          complete: contactInfoComplete
        },
        {
          value: 2,
          label: 'Loss Location',
          complete: lossLocationComplete
        },
        {
          value: 3,
          label: 'Loss Details',
          complete: lossDetailsComplete
        },
        {
          value: 4,
          label: 'Primary Vehicle',
          complete: primaryVehicleComplete
        },
        {
          value: 5,
          label: 'Driver Information',
          complete: driverDetailsComplete
        },
        {
          value: 6,
          label: 'Claimants',
          complete: true // Optional section
        }
      ]}
    />
  );

  return (
    <HeaderAndFooter
      Header={header}
      Footer={footer}
      Left={left}
      showFooter={!isSubmitted && !isReadOnly}
    >
      {isSubmitted ? (
        <TravelersAutoClaimCard
          name={0}
          claimSection={overviewInfo}
          title="Overview"
          viewOnly
        />
      ) : (
        <InstructionCard name={0} />
      )}
      <TravelersAutoClaimCard
        name={1}
        editing={creating || contactEditing}
        viewOnly={isReadOnly}
        claimSection={contactInfo}
        onChange={values => {
          setContactInfo(values);
          setUnsavedChanges(true);
        }}
        title="Contact Information"
        openEdit={setContactEditing}
        isSubmitted={isSubmitted}
        missingRequired={missingRequired}
        permissionUsers={users.filter(u =>
          permissions.map(p => p.userId).includes(u._id)
        )}
      />
      <TravelersAutoClaimCard
        name={2}
        editing={creating || lossLocationEditing}
        viewOnly={isReadOnly}
        claimSection={lossLocation}
        onChange={values => {
          setLossLocation(values);
          setUnsavedChanges(true);
        }}
        title="Loss Location"
        openEdit={setLossLocationEditing}
        isSubmitted={isSubmitted}
        missingRequired={missingRequired}
      />
      <TravelersAutoClaimCard
        name={3}
        editing={creating || lossDetailsEditing}
        viewOnly={isReadOnly}
        claimSection={lossDetails}
        onChange={values => {
          setLossDetails(values);
          setUnsavedChanges(true);
        }}
        title="Loss Details"
        openEdit={setLossDetailsEditing}
        isSubmitted={isSubmitted}
        missingRequired={missingRequired}
        dateOfLoss={dateOfLoss}
        setDateOfLoss={val => {
          setDateOfLoss(val);
          setUnsavedChanges(true);
        }}
        locationCode={locationCode}
        setLocationCode={val => {
          setLocationCode(val);
          setUnsavedChanges(true);
        }}
        locationCodes={locationCodes}
      />
      <TravelersAutoClaimCard
        name={4}
        editing={creating || primaryVehicleEditing}
        viewOnly={isReadOnly}
        claimSection={primaryVehicle}
        onChange={values => {
          setPrimaryVehicle(values);
          setUnsavedChanges(true);
        }}
        title="Primary Vehicle"
        openEdit={setPrimaryVehicleEditing}
        isSubmitted={isSubmitted}
        missingRequired={missingRequired}
      />
      <TravelersAutoClaimCard
        name={5}
        editing={creating || driverEditing}
        viewOnly={isReadOnly}
        claimSection={driverDetails}
        onChange={values => {
          setDriverDetails(values);
          setUnsavedChanges(true);
        }}
        title="Driver Information"
        openEdit={setDriverEditing}
        isSubmitted={isSubmitted}
        missingRequired={missingRequired}
      />
      <TravelersAutoClaimCard
        name={6}
        editing={creating || claimantsEditing}
        viewOnly={isReadOnly}
        claimSection={claimants}
        onChange={values => {
          setClaimants(values);
          setUnsavedChanges(true);
          // Auto-bump vehiclesInvolved to 2 when Type A (Other Vehicle) claimant is added
          const hasOtherVehicle = values.some(c => c.claimantType === 'A');
          const currentVehicles = parseInt(
            lossDetails.vehicleAccidentDetails?.vehiclesInvolved,
            10
          );
          if (hasOtherVehicle && (!currentVehicles || currentVehicles < 2)) {
            setLossDetails(prev => ({
              ...prev,
              vehicleAccidentDetails: {
                ...prev.vehicleAccidentDetails,
                vehiclesInvolved: 2
              }
            }));
          }
        }}
        title="Claimants"
        openEdit={setClaimantsEditing}
        isSubmitted={isSubmitted}
        missingRequired={missingRequired}
      />
      <SaveChangesModal
        onRequestClose={() => setSaveChangesModal(false)}
        isOpen={saveChangesModal}
        submitActions={() => handleCancel(true)}
      />
      <DeleteItemModal
        onRequestClose={() => setDeleteModal(false)}
        isOpen={deleteModal}
        submitActions={handleDelete}
        hideCustomTemplate
        what="Claim"
      />
      <Modal
        title="Submit Claim"
        titleClassName="blueHeader"
        isOpen={submitModal}
        submitButtonColor="blue"
        submitButtonText="Submit"
        onRequestClose={() => setSubmitModal(false)}
        submitActions={() => handleSubmit(true)}
      >
        <div className={styles.text}>
          Once you submit the claim the data will no longer update within the
          incident or for your insurance.
        </div>
      </Modal>
      <MissingRequiredModal
        isOpen={missingModalOpen}
        onRequestClose={() => setMissingModalOpen(false)}
        subject="Auto Claim"
      />
    </HeaderAndFooter>
  );
}
